import { useContext, useMemo } from 'react';
import { useRouter } from '../use-router';
import { LanguageCode, languageCodes } from 'shared/library/i18n/languages';
import { RouteContext } from './provider';
import {
  addRouteQuery,
  buildLocalizedRoutePath,
  RouteName,
  RouteParams,
  routesByName,
} from './route-registry';

type RouteOptions = {
  query?: Record<string, string | number>;
  lang?: LanguageCode;
  includeDomain?: boolean;
  preserveTabContext?: boolean;
};

export function buildRoute<K extends RouteName>(
  name: K,
  params?: RouteParams<K>,
  options?: RouteOptions,
): string {
  const language = options?.lang ?? 'en';
  const path = buildLocalizedRoutePath(name, params, language);

  const url = options?.includeDomain
    ? `https://getapet.com/${language}${path}`.replace(/\/$/, '')
    : `/${language}${path}`.replace(/\/$/, '');

  return addRouteQuery(url, options?.query);
}

export function useRoute() {
  const router = useRouter();

  return function route<K extends RouteName>(
    name: K,
    params?: RouteParams<K>,
    options?: RouteOptions,
  ): string {
    try {
      return buildRoute(name, params, { ...options, lang: options?.lang ?? router.languageCode });
    } catch (error) {
      console.error('useRoute', name, error);
      return '';
    }
  };
}

export function useStaticRoute() {
  const router = useRouter();

  return useMemo(
    () =>
      function route<K extends RouteName>(
        name: K,
        params?: RouteParams<K>,
        options?: RouteOptions,
      ): string {
        try {
          return buildRoute(name, params, {
            ...options,
            lang: options?.lang ?? router.languageCode,
          });
        } catch (error) {
          console.error('useStaticRoute', name, error);
          return '';
        }
      },
    [router.languageCode],
  );
}

export function useCurrentRoute() {
  const { pathname, query, asPath } = useRouter();
  const { params: richParams } = useContext(RouteContext);

  return useMemo(() => {
    const stripQueryAndHash = (path: string) => {
      const [withoutQuery = ''] = path.split('?');
      const [withoutHash = ''] = withoutQuery.split('#');
      return withoutHash;
    };
    const normalizePath = (path: string) => {
      const stripped = stripQueryAndHash(path);
      return stripped === '/' ? stripped : stripped.replace(/\/$/, '');
    };

    const normalizedPathname = normalizePath(pathname);
    const normalizedAsPath = normalizePath(asPath);

    for (const locale of languageCodes) {
      for (const r of Object.values(routesByName)) {
        try {
          const builtPath = r.build({ ...query, ...richParams } as any, locale);
          const normalizedBuiltPath = normalizePath(builtPath);
          const normalizedLocalizedBuiltPath = normalizePath(`/${locale}${builtPath}`);

          if (
            normalizedBuiltPath === normalizedPathname ||
            normalizedBuiltPath === normalizedAsPath ||
            normalizedLocalizedBuiltPath === normalizedAsPath
          ) {
            return {
              matched: true,
              name: r.name as RouteName,
              params: { ...query, ...richParams },
            };
          }
        } catch {}
      }
    }

    return {
      matched: false,
      name: 'home' as RouteName,
      params: query as any,
    };
  }, [asPath, pathname, query, richParams]);
}

export function useIsCurrentRoute() {
  const { asPath, languageCode } = useRouter();
  const route = useRoute();
  const currentRoute = useCurrentRoute();

  return function isCurrentRoute<K extends RouteName>(name: K, params?: RouteParams<K>) {
    const target = route(name, params);
    const currentPath = `/${languageCode}${asPath}`;
    const isHomeRoute = target === `/${languageCode}` || target === `/${languageCode}/`;
    const isCurrentPathMatch =
      currentPath === target ||
      currentPath.startsWith(`${target}/`) ||
      currentPath.startsWith(`${target}?`) ||
      currentPath.startsWith(`${target}#`);

    if (isHomeRoute) {
      return currentRoute.name === name;
    }

    if (params !== undefined) {
      return isCurrentPathMatch;
    }

    return currentRoute.name === name || isCurrentPathMatch;
  };
}

export type { RouteName };
